import { UIPaths } from '@consts';
import { ChevronDownIcon, ChevronUpIcon, LoaderIcon } from '@icons';
import { GoalCardLinkTarget, GoalModel, GoalType, ToastType } from '@models';
import { Header } from '@shared';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import { useIntersectionObserver } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { useGoalsService } from 'lib/hooks/useGoalsService';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import GoalCard from './GoalCard';

// Animation keyframes for expand/collapse
const animationStyles = `
  @keyframes fadeSlideDown {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fadeSlideUp {
    to {
      opacity: 0;
      transform: translateY(-20px);
    }
  }
`;

const GoalContentList = ({
  goal,
  className,
  clickTarget = GoalCardLinkTarget.GOAL
}: {
  goal: GoalModel;
  className?: string;
  clickTarget?: GoalCardLinkTarget;
}) => {
  const { t } = useTranslation();
  const { getGoals } = useGoalsService();
  const [contentList, setContentList] = useState<GoalModel[]>([]);
  const [wasVisible, setWasVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemsListState, setItemsListState] = useState({
    hasRemainingItems: false,
    showRemainingItems: false,
    isExiting: false
  });
  const [ref, entry] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  });
  const { setToast } = useStore((state) => ({ setToast: state.setToast }), shallow);

  useEffect(() => {
    if (goal.type === GoalType.COMBO && wasVisible) {
      setIsLoading(true);
      getGoals({ comboGoalEntityId: goal.entityId })
        .then(({ goals }) => {
          setContentList(goals);
          setItemsListState({
            hasRemainingItems: goals.length > 3,
            showRemainingItems: false,
            isExiting: false
          });
        })
        .catch((error) => {
          console.debug(error);
          setContentList([]);
          setItemsListState({
            hasRemainingItems: false,
            showRemainingItems: false,
            isExiting: false
          });
          setToast({
            message: t('errors.somethingWentWrong'),
            type: ToastType.ERROR
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setContentList([]);
      setItemsListState({
        hasRemainingItems: false,
        showRemainingItems: false,
        isExiting: false
      });
    }
  }, [goal.entityId, wasVisible]);

  useEffect(() => {
    if (entry?.isIntersecting) {
      setWasVisible(true);
    }
  }, [entry?.isIntersecting]);

  if (goal.type !== GoalType.COMBO) {
    // TODO JUST FOR NOW BERORE WE WILL ADD SAMPLE TASKS FOR SINGLE GOALS
    return null;
  }

  const handleShowMore = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setItemsListState((state) => ({
      ...state,
      showRemainingItems: true,
      isExiting: false
    }));
  };

  const handleShowLess = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setItemsListState((state) => ({
      ...state,
      isExiting: true
    }));
    setTimeout(() => {
      setItemsListState((state) => ({
        ...state,
        showRemainingItems: false
      }));
    }, 400); // Match animation duration
  };

  // Split contentList into always-visible and expandable parts
  const headListItems = contentList.slice(0, 3);
  const remainingListItems = contentList.slice(3);

  return (
    <div
      data-testid={`goalContentList-goal-${goal.entityId}`}
      ref={ref}
      className={classNames('flex flex-col', className)}
    >
      <Header
        title={t('domains.goals.contentListTitle')}
        withUnderline={false}
        bold
        className='items-center justify-center'
        data-testid={`goalContentList-title-goal-${goal.entityId}`}
      />
      <div
        data-testid={`goalContentList-items-goal-${goal.entityId}`}
        className='flex flex-col gap-sm'
      >
        {isLoading ? (
          <div className='flex flex-col gap-sm w-full justify-center items-center'>
            <LoaderIcon size={24} />
          </div>
        ) : (
          <>
            {/* Always show the first 3 items */}
            {headListItems.map((goal, index) => (
              <GoalCard
                orderedLabel={index + 1}
                key={goal.entityId}
                goal={goal}
                showActions={{}}
                goalSubscription={goal?.subscription}
                customLink={
                  clickTarget === GoalCardLinkTarget.SUBSCRIPTION && goal?.subscription
                    ? UIPaths.goalSubscription(goal.subscription.id)
                    : UIPaths.goal(goal.entityId)
                }
              />
            ))}
            {/* Animate the expandable items */}
            {itemsListState.showRemainingItems && remainingListItems.length > 0 && (
              <div
                className='flex flex-col gap-sm overflow-hidden'
                style={{
                  opacity: itemsListState.isExiting ? 1 : 0,
                  transform: itemsListState.isExiting ? 'translateY(0)' : 'translateY(-20px)',
                  animation: itemsListState.isExiting
                    ? `fadeSlideUp 0.3s ease-out forwards`
                    : `fadeSlideDown 0.3s ease-out forwards`
                }}
              >
                {remainingListItems.map((goal, index) => (
                  <GoalCard
                    orderedLabel={index + 4}
                    key={goal.entityId}
                    goal={goal}
                    showActions={{}}
                    goalSubscription={goal?.subscription}
                    customLink={
                      clickTarget === GoalCardLinkTarget.SUBSCRIPTION && goal?.subscription
                        ? UIPaths.goalSubscription(goal.subscription.id)
                        : UIPaths.goal(goal.entityId)
                    }
                  />
                ))}
                <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
              </div>
            )}
          </>
        )}
        {itemsListState.hasRemainingItems && !itemsListState.showRemainingItems && (
          <div
            className={classNames(
              'flex flex-row gap-xxs w-full justify-center items-center cursor-pointer',
              setupForDark('text-primary')
            )}
            data-testid={`goalContentList-showMore-goal-${goal.entityId}`}
            onClick={handleShowMore}
          >
            {t('domains.goals.contentListShowMore')}
            <ChevronDownIcon size={16} />
          </div>
        )}
        {itemsListState.hasRemainingItems && itemsListState.showRemainingItems && (
          <div
            data-testid={`goalContentList-showLess-goal-${goal.entityId}`}
            className={classNames(
              'flex flex-row gap-xxs w-full justify-center items-center cursor-pointer',
              setupForDark('text-primary')
            )}
            onClick={handleShowLess}
          >
            {t('domains.goals.contentListShowLess')}
            <ChevronUpIcon size={16} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalContentList;
